import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { getDynamicTranslationForArray } from '@monorepo/client-common';
import { trackMixpanel } from '@monorepo/client-common';
import { ButtonText, PagePath } from '@monorepo/client-types';
import { LoadingState, useLoading } from '@monorepo/react-hooks';
import {
  AffiliateUserType,
  AnalyticsEventTypes,
  ContractType,
  CreateAffiliateRequestType,
  EventTypes,
} from '@monorepo/types';
import { useCreateAffiliateRequest } from '@hub/api/mutations';
import { useStore } from '@hub/helpers/use-store';
import gtmService from '../../../../../services/analytics/analytics';
import { getSocialMediaFromLink } from '../../../affiliation-modal-content/social-media-input';
import { AffiliationRegisterFormValues } from '../register-affiliate-modal';

/**
 * Custom hook for managing RegisterAffiliateModal component state
 * Handles form management, data fetching, submission logic, and loading states
 */
export const useRegisterAffiliateModal = (onClose: () => void) => {
  const {
    dataStore: { userStore, contractStore, applicationConfigStore },
  } = useStore();
  const { updateLoadingState, isLoading, isLoadError } = useLoading();
  const { t } = useTranslation(['affiliate', 'common']);
  const { affiliateTermsAndConditions, i18nArrays } =
    applicationConfigStore.applicationConfig;
  const { mutateAsync: createAffiliateRequest } = useCreateAffiliateRequest();

  const form = useForm<AffiliationRegisterFormValues>({
    defaultValues: {
      type: AffiliateUserType.Affiliate,
      acceptedTermsAndConditions: false,
      socialMediaLinks: [{ link: '' }],
    },
    mode: 'onTouched',
  });

  const acceptedTermsAndConditions = form.watch('acceptedTermsAndConditions');

  const rules = useMemo(() => {
    if (!affiliateTermsAndConditions?.length) return [];

    const i18nEntries = i18nArrays?.['affiliateTermsAndConditions'];

    return affiliateTermsAndConditions
      .sort((a, b) => a.order - b.order)
      .map((term) =>
        getDynamicTranslationForArray(
          term.term || '',
          i18nEntries || [],
          term.id,
          'term',
          t,
        ),
      )
      .filter(Boolean);
  }, [affiliateTermsAndConditions, i18nArrays, t]);

  const formatFormData = useCallback(
    ({
      type,
      message,
      socialMediaLinks,
      acceptedTermsAndConditions,
    }: AffiliationRegisterFormValues): CreateAffiliateRequestType => ({
      type,
      message,
      socialMediaProfiles: socialMediaLinks
        .filter(({ link }) => !!link)
        .map(({ link }) => ({
          platform: getSocialMediaFromLink(link),
          link,
        })),
      acceptedTermsAndConditions,
    }),
    [],
  );

  const onSubmit = useCallback(
    async (data: AffiliationRegisterFormValues) => {
      updateLoadingState(LoadingState.Loading);
      try {
        await contractStore.signUserContract(ContractType.Affiliation);
        const affiliateId = await userStore.registerAsAffiliate();
        if (affiliateId && data.type !== AffiliateUserType.Affiliate) {
          await createAffiliateRequest(formatFormData(data));
        }
        if (affiliateId) {
          gtmService.sendEvent({
            event: AnalyticsEventTypes.RegisterAsAffiliate,
            params: { button_text: ButtonText.Register },
          });
          let redirected = false;
          const redirectToDashboard = () => {
            if (redirected) return;
            redirected = true;
            onClose();
            window.location.href = PagePath.Affiliation;
          };

          trackMixpanel({
            event: EventTypes.AFFILIATE_TERMS_ACCEPTED,
            properties: {
              user_id: userStore.currentUser?.userId,
            },
            callback: redirectToDashboard,
          });

          // Fallback: redirect if Mixpanel callback never fires (e.g. not initialized)
          setTimeout(redirectToDashboard, 500);
        } else {
          updateLoadingState(LoadingState.Error);
        }
      } catch {
        updateLoadingState(LoadingState.Error);
      }
    },
    [
      contractStore,
      userStore,
      createAffiliateRequest,
      formatFormData,
      onClose,
      updateLoadingState,
    ],
  );

  const handleClose = useCallback(() => {
    form.reset();
    onClose();
  }, [form, onClose]);

  return {
    form,
    acceptedTermsAndConditions,
    rules,
    isLoading,
    isLoadError,
    onSubmit,
    handleClose,
  };
};
